import * as R from 'remeda';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import {
  Autocomplete,
  autocompleteClasses,
  Chip,
  TextField,
} from '@rossum/ui/material';

type PermissionScopesControlProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  helperText: string;
  disabled?: boolean;
};

/** Trims each scope and drops blanks and repeats, so every chip is distinct. */
const normalizeScopes = (scopes: readonly string[]) =>
  R.pipe(
    scopes,
    R.map(scope => scope.trim()),
    R.filter(Boolean),
    R.unique()
  );

/**
 * Scopes are not offered by the API, users type them by hand - a free solo
 * autocomplete turns each confirmed entry into a chip.
 */
export const PermissionScopesControl = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  helperText,
  disabled,
}: PermissionScopesControlProps<TFieldValues>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const values = normalizeScopes(field.value ?? []);

        return (
          <Autocomplete<string, true, false, true>
            multiple
            freeSolo
            fullWidth
            disabled={disabled}
            options={[]}
            value={values}
            onChange={(_, newValues) =>
              field.onChange(normalizeScopes(newValues))
            }
            onBlur={field.onBlur}
            renderValue={(scopes, getTagProps) =>
              scopes.map((scope, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={scope}
                  label={scope}
                  size="small"
                  sx={{ [`&.${autocompleteClasses.tag}`]: { m: 0.25 } }}
                />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                id={field.name}
                inputRef={field.ref}
                label={label}
                size="small"
                error={fieldState.invalid}
                helperText={
                  fieldState.invalid && fieldState.error
                    ? fieldState.error.message
                    : helperText
                }
              />
            )}
          />
        );
      }}
    />
  );
};
